import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import { Loader2, MailPlus, MailWarning, Copy, Check, Send } from "lucide-react";

const ALL_ROLES = ["worker", "approver", "admin", "tenant_admin"];
// Show the Resend button on non-accepted, non-expired rows whose last
// send is either failed OR at least this old (silent-recipient nudge).
const RESEND_STALE_AFTER_MS = 60 * 60 * 1000; // 1 hour

function timeAgo(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const secs = Math.max(0, (Date.now() - then) / 1000);
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)} min ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)} hr ago`;
  return `${Math.floor(secs / 86400)} d ago`;
}

function inviteShouldOfferResend(inv) {
  if (!inv || inv.redeemed_at) return false;
  const expiresAtMs = inv.expires_at ? new Date(inv.expires_at).getTime() : 0;
  if (expiresAtMs && expiresAtMs < Date.now()) return false;
  if (inv.email_send_error) return true;
  const lastMs = inv.email_last_sent_at
    ? new Date(inv.email_last_sent_at).getTime()
    : 0;
  return lastMs > 0 && (Date.now() - lastMs) >= RESEND_STALE_AFTER_MS;
}

export default function AdminInvitesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tenant/invites");
      setItems(data.items);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="admin-invites-page">
      <div>
        <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Administration</div>
        <h1 className="mt-1 font-display text-3xl font-semibold text-primary">Invites</h1>
        <p className="mt-2 text-muted-foreground">Invite teammates by email. They'll receive a magic link to activate.</p>
      </div>

      <div>
        <NewInviteDialog onCreated={load} />
      </div>

      <Card>
        <CardHeader><CardTitle>All invites</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Roles</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Activity</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="invites-tbody">
              {loading && <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">Loading…</TableCell></TableRow>}
              {!loading && items.length === 0 && <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">No invites yet.</TableCell></TableRow>}
              {items.map((i) => (
                <TableRow key={i.id} data-testid={`invite-row-${i.id}`}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {i.email}
                      {i.email_send_error && !i.redeemed_at && (
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                data-testid={`invite-email-failed-${i.id}`}
                                className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-amber-500/15 text-amber-700"
                                aria-label="Email delivery failed"
                              >
                                <MailWarning className="h-3 w-3" />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs">
                              <div className="text-xs font-semibold">Email never delivered</div>
                              <div className="mt-1 text-xs italic text-muted-foreground">
                                {i.email_send_error}
                              </div>
                              <div className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">
                                The invite token is still valid — resend from the Actions column or copy the accept link.
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {i.roles.map((r) => (<Badge key={r} variant="secondary" className="rounded-full">{r}</Badge>))}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{new Date(i.expires_at).toLocaleString()}</TableCell>
                  <TableCell className="text-xs text-muted-foreground" data-testid={`invite-activity-${i.id}`}>
                    {i.email_last_sent_at ? (
                      <>
                        Sent {timeAgo(i.email_last_sent_at)}
                        {" · "}
                        Attempts: <span className="font-medium">{i.email_send_count ?? 1}</span>
                      </>
                    ) : (
                      <span className="italic">Not yet sent</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {i.redeemed_at ? (
                      <Badge className="bg-success/15 text-success hover:bg-success/15">Redeemed</Badge>
                    ) : new Date(i.expires_at) < new Date() ? (
                      <Badge variant="outline" className="text-muted-foreground">Expired</Badge>
                    ) : i.email_send_error ? (
                      <Badge className="bg-amber-500/15 text-amber-700 hover:bg-amber-500/15" data-testid={`invite-status-email-failed-${i.id}`}>
                        Pending · email failed
                      </Badge>
                    ) : (
                      <Badge className="bg-warning/15 text-warning-foreground hover:bg-warning/15">Pending</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {inviteShouldOfferResend(i) && (
                      <ResendInviteAction invite={i} onDone={load} />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function ResendInviteAction({ invite, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [failure, setFailure] = useState(null);
  // { accept_url, error } on Resend send-failure fallback

  const nextAttempt = (invite.email_send_count ?? 1) + 1;

  const parseRetryAfter = (err) => {
    const h = err?.response?.headers?.["retry-after"];
    if (h && !Number.isNaN(Number(h))) return Number(h);
    return null;
  };

  const doResend = async () => {
    setBusy(true);
    setFailure(null);
    try {
      const { data } = await api.post(`/tenant/invites/${invite.id}/resend`, {});
      const er = data?.email_result;
      if (er?.sent === false) {
        toast.warning(
          `Email still failed — ${er.error || "unknown error"}. Copy the link and send manually.`,
        );
        setFailure({
          accept_url: data.accept_url,
          error: er.error || "unknown error",
        });
        onDone && onDone();
        return;
      }
      toast.success(`Invite email resent to ${invite.email}`);
      setOpen(false);
      onDone && onDone();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 429) {
        const ra = parseRetryAfter(e);
        toast.error(
          ra
            ? `Rate-limited — try again in ${ra} seconds.`
            : "Rate-limited — try again shortly.",
        );
      } else if (status === 410) {
        toast.error("Invite expired — create a new one instead.");
        setDisabled(true);
        setOpen(false);
        onDone && onDone();
      } else if (status === 409) {
        toast.success("Invite already accepted — nothing to send.");
        setOpen(false);
        onDone && onDone();
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const closeDialog = () => {
    setOpen(false);
    setFailure(null);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : closeDialog())}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          disabled={disabled}
          data-testid={`invite-resend-btn-${invite.id}`}
        >
          <Send className="h-3 w-3" /> Resend
        </Button>
      </DialogTrigger>
      <DialogContent data-testid={`invite-resend-dialog-${invite.id}`}>
        <DialogHeader>
          <DialogTitle>Resend invite email</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {!failure && (
            <>
              <div className="text-sm">
                Resend invite email to <span className="font-mono">{invite.email}</span>?
              </div>
              <div className="text-xs text-muted-foreground">
                This will be attempt #<span className="font-medium">{nextAttempt}</span>.
                The accept link stays the same — the recipient can still
                use the original URL if they already have it. Rate-limited to
                5 sends per hour.
              </div>
              {invite.email_send_error && (
                <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-900">
                  <div className="font-semibold">Previous error</div>
                  <div className="mt-1 italic">{invite.email_send_error}</div>
                </div>
              )}
            </>
          )}
          {failure && (
            <Alert
              className="border-amber-500/40 bg-amber-500/5"
              data-testid={`invite-resend-fallback-${invite.id}`}
            >
              <MailWarning className="h-4 w-4 text-amber-700" />
              <AlertDescription className="ml-6 space-y-2">
                <div className="text-sm font-semibold text-amber-800">
                  Email still couldn't be delivered
                </div>
                <div className="text-xs italic text-muted-foreground">
                  Reason: {failure.error}
                </div>
                <div className="text-sm">
                  The invite token is still valid — copy the link and share it manually.
                </div>
                <div
                  className="rounded-md border bg-white p-2 font-mono text-xs break-all"
                  data-testid={`invite-resend-accept-url-${invite.id}`}
                >
                  {failure.accept_url}
                </div>
                <div>
                  <CopyLinkButton
                    url={failure.accept_url}
                    testid={`invite-resend-copy-link-${invite.id}`}
                  />
                </div>
              </AlertDescription>
            </Alert>
          )}
        </div>
        <DialogFooter>
          {failure ? (
            <Button
              onClick={closeDialog}
              data-testid={`invite-resend-done-${invite.id}`}
            >
              Done
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={closeDialog}>Cancel</Button>
              <Button
                onClick={doResend}
                disabled={busy}
                data-testid={`invite-resend-confirm-${invite.id}`}
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                Resend email
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CopyLinkButton({ url, testid = "copy-invite-link" }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Invite link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (_e) {
      // Fallback for browsers without clipboard API.
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); toast.success("Invite link copied"); }
      catch { toast.error("Could not copy — select and copy manually"); }
      document.body.removeChild(ta);
    }
  };
  return (
    <Button variant="outline" size="sm" onClick={copy} data-testid={testid}>
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      {copied ? "Copied" : "Copy invite link"}
    </Button>
  );
}

function NewInviteDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [full_name, setFullName] = useState("");
  const [roles, setRoles] = useState(["worker"]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Phase 27b hotfix — when the mailer fails, we don't close the
  // dialog. Instead we show the accept URL + a copy button so the
  // admin can share it manually.
  const [fallback, setFallback] = useState(null);
  // shape: { accept_url, email, error }

  const toggle = (r) => setRoles((prev) => prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]);

  const reset = () => {
    setEmail(""); setFullName(""); setRoles(["worker"]);
    setError(""); setFallback(null);
  };

  const close = () => {
    setOpen(false);
    reset();
    onCreated && onCreated();
  };

  const send = async () => {
    setBusy(true); setError(""); setFallback(null);
    try {
      const { data } = await api.post("/tenant/invites", {
        email: email.trim(), full_name, roles,
      });
      const er = data?.email_result;
      if (er && er.sent === false) {
        // Invite created, email delivery failed. Surface the fallback.
        toast.warning(
          `Invite created but email failed — ${er.error || "unknown error"}. Copy the link and send manually.`,
        );
        setFallback({
          accept_url: data.accept_url,
          email: data.email,
          error: er.error || "unknown error",
        });
        // Refresh list in the background so the row shows "email failed".
        onCreated && onCreated();
      } else {
        toast.success(`Invite sent to ${email}`);
        setOpen(false);
        reset();
        onCreated && onCreated();
      }
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
      <DialogTrigger asChild>
        <Button data-testid="new-invite-btn"><MailPlus className="h-4 w-4" /> Invite a teammate</Button>
      </DialogTrigger>
      <DialogContent data-testid="new-invite-dialog">
        <DialogHeader><DialogTitle>Invite a teammate</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ie">Email</Label>
            <Input id="ie" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@company.com" data-testid="invite-email" disabled={!!fallback} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="in">Full name (optional)</Label>
            <Input id="in" value={full_name} onChange={(e) => setFullName(e.target.value)} data-testid="invite-name" disabled={!!fallback} />
          </div>
          <div className="space-y-2">
            <Label>Roles</Label>
            <div className="grid grid-cols-2 gap-2">
              {ALL_ROLES.map((r) => (
                <label key={r} className="flex items-center gap-2 rounded-md border p-2 text-sm" data-testid={`invite-role-${r}`}>
                  <Checkbox checked={roles.includes(r)} onCheckedChange={() => toggle(r)} disabled={!!fallback} />
                  <span>{r}</span>
                </label>
              ))}
            </div>
          </div>
          {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
          {fallback && (
            <Alert
              className="border-amber-500/40 bg-amber-500/5"
              data-testid="invite-email-fallback"
            >
              <MailWarning className="h-4 w-4 text-amber-700" />
              <AlertDescription className="ml-6 space-y-3">
                <div>
                  <div className="text-sm font-semibold text-amber-800">
                    Email couldn't be delivered
                  </div>
                  <div className="mt-1 text-xs italic text-muted-foreground">
                    Reason: {fallback.error}
                  </div>
                  <div className="mt-2 text-sm">
                    The invite is valid. Copy the link below and share it
                    with <span className="font-mono">{fallback.email}</span> manually.
                  </div>
                </div>
                <div className="rounded-md border bg-white p-2 font-mono text-xs break-all" data-testid="invite-accept-url">
                  {fallback.accept_url}
                </div>
                <div>
                  <CopyLinkButton url={fallback.accept_url} testid="invite-copy-link" />
                </div>
              </AlertDescription>
            </Alert>
          )}
        </div>
        <DialogFooter>
          {fallback ? (
            <Button onClick={close} data-testid="invite-fallback-done">Done</Button>
          ) : (
            <>
              <Button variant="outline" onClick={close}>Cancel</Button>
              <Button onClick={send} disabled={busy || !email || roles.length === 0} data-testid="invite-send-btn">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Send invite
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
